import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Eye, MessageCircle, Search, Grid, List, Package, Play, Pause } from 'lucide-react';

export default function MyProducts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    // Allow all users (BUYER, SELLER, ADMIN) to view their products
    fetchProducts();
  }, [user]);

  const fetchProducts = async () => {
    try {
      const { data } = await api.get('/my/products');
      setProducts(data);
    } catch {} finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
    } catch {}
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await api.put(`/products/${id}`, { status: newStatus });
      fetchProducts();
    } catch {}
  };

  // Apply filters
  const filteredProducts = products.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (conditionFilter !== 'all' && p.condition !== conditionFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p.title?.toLowerCase().includes(q) && !p.id?.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
      case 'price_high': return b.price - a.price;
      case 'price_low': return a.price - b.price;
      case 'views': return (b.views || 0) - (a.views || 0);
      default: return 0;
    }
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-700 border-green-300';
      case 'PENDING': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'PAUSED': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'SOLD': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'DELETED': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return '✅ Activo';
      case 'PENDING': return '⏳ Pendiente';
      case 'PAUSED': return '⏸ Pausado';
      case 'SOLD': return '💰 Vendido';
      case 'DELETED': return '❌ Eliminado';
      default: return status;
    }
  };

  const countByStatus = (status) => products.filter(p => p.status === status).length;

  const getProductLimit = () => {
    const limits = { FREE: 5, PRO: 20, PREMIUM: 999999 };
    return limits[user?.subscription_plan] || 5;
  };

  const currentCount = products.filter(p => ['ACTIVE', 'PAUSED'].includes(p.status)).length;
  const maxProducts = getProductLimit();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-black">Mis Productos</h1>
                <p className="text-sm text-gray-500">
                  {currentCount}/{maxProducts} productos 
                  {user?.subscription_plan !== 'FREE' && (
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                      user?.subscription_plan === 'PREMIUM' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {user?.subscription_plan}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-gray-100'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-gray-100'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg font-medium ${showFilters ? 'bg-primary text-white' : 'bg-gray-100'}`}
              >
                Filtros
              </button>
              
              {currentCount < maxProducts && (
                <Link 
                  to="/seller/products/new" 
                  className="bg-primary text-white px-5 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-primary-dark"
                >
                  <Plus className="w-5 h-5" />
                  Nuevo
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar productos por nombre o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ordenar</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="newest">Más recientes</option>
                  <option value="oldest">Más antiguos</option>
                  <option value="price_high">Precio mayor</option>
                  <option value="price_low">Precio menor</option>
                  <option value="views">Más vistas</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Estado</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="all">Todos</option>
                  <option value="ACTIVE">Activos</option>
                  <option value="PENDING">Pendientes</option>
                  <option value="PAUSED">Pausados</option>
                  <option value="SOLD">Vendidos</option>
                  <option value="DELETED">Eliminados</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Condición</label>
                <select
                  value={conditionFilter}
                  onChange={(e) => setConditionFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="all">Todas</option>
                  <option value="NEW">Nuevo</option>
                  <option value="USED">Usado</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setConditionFilter('all');
                  setSortBy('newest');
                  setSearchQuery('');
                }}
                className="text-sm text-gray-500 hover:text-red-500"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        )}

        {/* Status Quick Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-full font-medium ${statusFilter === 'all' ? 'bg-black text-white' : 'bg-gray-100'}`}
          >
            Todos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{products.length}</span>
          </button>
          <button
            onClick={() => setStatusFilter('ACTIVE')}
            className={`px-4 py-2 rounded-full font-medium ${statusFilter === 'ACTIVE' ? 'bg-green-500 text-white' : 'bg-green-50'}`}
          >
            ✅ Activos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('ACTIVE')}</span>
          </button>
          <button
            onClick={() => setStatusFilter('PENDING')}
            className={`px-4 py-2 rounded-full font-medium ${statusFilter === 'PENDING' ? 'bg-yellow-500 text-black' : 'bg-yellow-50'}`}
          >
            ⏳ Pendientes <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('PENDING')}</span>
          </button>
          <button
            onClick={() => setStatusFilter('PAUSED')}
            className={`px-4 py-2 rounded-full font-medium ${statusFilter === 'PAUSED' ? 'bg-gray-500 text-white' : 'bg-gray-50'}`}
          >
            ⏸ Pausados <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('PAUSED')}</span>
          </button>
          <button
            onClick={() => setStatusFilter('SOLD')}
            className={`px-4 py-2 rounded-full font-medium ${statusFilter === 'SOLD' ? 'bg-blue-500 text-white' : 'bg-blue-50'}`}
          >
            💰 Vendidos <span className="bg-black/20 px-2 rounded-full text-xs ml-1">{countByStatus('SOLD')}</span>
          </button>
        </div>

        {/* Products Grid/List */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No hay productos que coincidan' : 'No tienes productos'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' ? 'Intenta con otros filtros' : '¡Publica tu primer repuesto!'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Link to="/seller/products/new" className="bg-primary text-white px-6 py-2 rounded-full font-bold inline-block">
                Publicar producto
              </Link>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map(product => (
              <div 
                key={product.id} 
                className={`bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition border-2 ${
                  product.status === 'PENDING' ? 'border-yellow-400' :
                  product.status === 'SOLD' ? 'border-blue-300' :
                  'border-transparent'
                }`}
              >
                {/* Image */}
                <div className="relative aspect-square bg-gray-100">
                  {product.images?.[0]?.url ? (
                    <img src={product.images[0].url} alt={product.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">🛞</div>
                  )}
                  
                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {product.condition === 'NEW' && (
                      <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded font-bold">Nuevo</span>
                    )}
                    {(user?.subscription_plan === 'PRO' || user?.subscription_plan === 'PREMIUM') && (
                      <span className="bg-yellow-400 text-black text-xs px-2 py-0.5 rounded font-bold">⭐</span>
                    )}
                  </div>
                  
                  {/* Status */}
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getStatusBadge(product.status)}`}>
                      {getStatusLabel(product.status)}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.title}</h3>
                  <p className="text-xs text-gray-500 mb-2">{product.category?.brand} {product.category?.model}</p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-primary">₡{Number(product.price).toLocaleString()}</span>
                    {product.negotiable && <span className="text-xs text-gray-400">Negociable</span>}
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pb-3 border-b">
                    <span>👁 {product.views || 0}</span>
                    <span>📷 {product.images?.length || 0}</span>
                    <span>{product.province?.replace('_', ' ')}</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="grid grid-cols-4 gap-2">
                    <button
                      onClick={() => toggleStatus(product.id, product.status)}
                      className={`p-2 rounded-lg text-sm font-medium ${
                        product.status === 'ACTIVE' 
                          ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' 
                          : 'bg-green-100 hover:bg-green-200 text-green-600'
                      }`}
                      title={product.status === 'ACTIVE' ? 'Pausar' : 'Activar'}
                    >
                      {product.status === 'ACTIVE' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    
                    <Link
                      to={`/seller/products/${product.id}`}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg text-center"
                      title="Gestionar"
                    >
                      <MessageCircle className="w-4 h-4 mx-auto" />
                    </Link>
                    
                    <Link
                      to={`/products/${product.id}`}
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg"
                      title="Ver"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Date */}
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    {new Date(product.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Producto</th>
                    <th className="text-left p-4 font-semibold">Precio</th>
                    <th className="text-left p-4 font-semibold">Estado</th>
                    <th className="text-left p-4 font-semibold">Stats</th>
                    <th className="text-left p-4 font-semibold">Fecha</th>
                    <th className="text-left p-4 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.map(product => (
                    <tr key={product.id} className={`hover:bg-gray-50 ${product.status === 'DELETED' ? 'opacity-50' : ''}`}>
                      <td className="p-4">
                        <Link to={`/products/${product.id}`} className="flex items-center gap-3 hover:text-primary">
                          {product.images?.[0]?.url ? (
                            <img src={product.images[0].url} alt={product.title} className="w-14 h-14 object-cover rounded-lg" />
                          ) : (
                            <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">🛞</div>
                          )}
                          <div>
                            <p className="font-medium line-clamp-1">{product.title}</p>
                            <p className="text-xs text-gray-500">{product.category?.brand} {product.category?.model}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="p-4 font-bold">₡{Number(product.price).toLocaleString()}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusBadge(product.status)}`}>
                          {getStatusLabel(product.status)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-3 text-sm text-gray-500">
                          <span>👁 {product.views || 0}</span>
                          <span>📷 {product.images?.length || 0}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {new Date(product.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleStatus(product.id, product.status)}
                            className={`p-2 rounded-lg ${
                              product.status === 'ACTIVE' 
                                ? 'bg-gray-100 hover:bg-gray-200' 
                                : 'bg-green-100 hover:bg-green-200 text-green-600'
                            }`}
                          >
                            {product.status === 'ACTIVE' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </button>
                          
                          <Link
                            to={`/seller/products/${product.id}`}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Link>
                          
                          <Link
                            to={`/products/${product.id}`}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          
                          <button
                            onClick={() => deleteProduct(product.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}